"use client";
import { useState, useEffect, useCallback, Suspense } from "react";
import { usePathname, useRouter } from "next/navigation";
import { subscribeToUser } from "@/app/_firebase/user";
import { useAuth } from "@/app/_hooks/useAuth";
import { setImpersonation } from "@/app/_utils/authenticatedFetch";
import {
  useSetUserContext,
  useNullableUserContext,
  useSetNullableUserContext,
  useUserContext,
} from "@/app/_hooks/useUserContext";
import { DatesProvider } from "@mantine/dates";
import AnimatedLoader from "@/app/_components/AnimatedLoader/AnimatedLoader";
import AppShell from "@/app/_components/AppShell/AppShell";
import { NavigationEvents } from "@/app/_components/NavigationEvents/NavigationEvents";
import Alert from "@/app/_components/Alert/Alert";
import { isPosTriggerEnabled } from "@/app/_helpers/user";
import { recordLastKnownUser } from "@/app/_helpers/errorBreadcrumbs";
import { POSTrigger } from "./_components/POSTrigger/POSTrigger";
import TimeClockWidget from "./_components/TimeClock/TimeClockWidget";
import { ChristmasModal } from "./_components/ChristmasModal/ChristmasModal";
import { NamedUserUID } from "./@types/enums";
import { User } from "./@types/user";
import { BillingOverdueModal } from "./_components/BillingOverdueModal/BillingOverdueModal";
import { BillingMigrationModal } from "./_components/BillingMigrationModal/BillingMigrationModal";
import { pendingSignupUrl, safeSignupReturnUrl } from "./_constants/signup";
import { useIsSubscriptionReady } from "./_hooks/subscription/useSubscriptionContext";
import MarketingConversionTracker from "./_components/MarketingConversionTracker/MarketingConversionTracker";

export default function Layout({ children }: { children: React.ReactNode }) {
  const { loading, authedUser } = useAuth();
  const setUser = useSetUserContext();
  const user = useUserContext();
  const possibleUser = useNullableUserContext();
  const setPossibleUser = useSetNullableUserContext();

  const pathname = usePathname();
  const router = useRouter();
  const [isNavigationComplete, setIsNavigationComplete] = useState(false);
  const markNavigationComplete = useCallback(
    () => setIsNavigationComplete(true),
    []
  );
  const isSubscriptionReady = useIsSubscriptionReady();

  // ADMIN IMPERSONATION - Comment/uncomment as needed
  // setImpersonation(NamedUserUID.WHITEBONE);

  // Latest snapshot of the user doc: undefined until the first snapshot, null
  // when the doc does not exist.
  const [userDoc, setUserDoc] = useState<User | null | undefined>(undefined);
  const isBookRoute = !!pathname?.startsWith("/book/");

  useEffect(() => {
    // Skip auth for public booking pages (shouldn't reach here but safety check)
    if (isBookRoute) {
      setIsNavigationComplete(true);
      return;
    }

    if (loading) return;

    if (!authedUser) {
      if (pathname === "/reset") {
        return;
      }
      if (pathname !== "/login") {
        const returnUrl =
          pathname && pathname !== "/"
            ? `?returnUrl=${encodeURIComponent(
                pathname + window.location.search
              )}`
            : "";
        // Hard navigation, not router.push. The QueryClient lives for the life
        // of the tab and most query keys are not scoped by account (["team"],
        // ["clients","all"], ["user","priceList"], ["jobs", jobNumber]...), so
        // a soft push would carry this account's cached data into whoever signs
        // in next on this browser. Reloading drops the whole JS heap instead.
        // Clearing the cache in place is not an option: PermissionsProvider
        // gates its children on its own query, so wiping it unmounts the
        // provider tree and remounts AccountingProvider, which then refetches
        // Firestore with no auth and shows a spurious error.
        window.location.assign(`/login${returnUrl}`);
      }
    }
  }, [authedUser, loading, isBookRoute, pathname]);

  // One listener per signed-in user. Keep pathname out of the deps: a fresh
  // listener on every page change re-published a new user object and
  // re-rendered the whole app right after each navigation (the stutter).
  useEffect(() => {
    if (isBookRoute || loading || !authedUser) return;

    const unsubscribe = subscribeToUser(
      authedUser.uid,
      (user) => {
        setUserDoc(user);
        if (!user) return;

        setPossibleUser(user);
        // Pending shops only render the signup pages. Do not publish them to
        // the business providers, which would fetch data before onboarding.
        if (user.signup?.status === "pending") return;

        setUser(user);
        recordLastKnownUser({
          userId: user.uid,
          businessName: user.settings?.name ?? "",
          email: user.email ?? "",
          authEmail: authedUser.email ?? undefined,
        });
      },
      (error) => {
        console.log({ error });
      }
    );

    return () => {
      unsubscribe();
      setUserDoc(undefined);
    };
  }, [authedUser, loading, isBookRoute, setPossibleUser, setUser]);

  // Route guards, re-checked on every page change and every user snapshot.
  useEffect(() => {
    if (isBookRoute || loading || !authedUser || userDoc === undefined) {
      return;
    }

    if (!userDoc) {
      if (pathname !== "/login") {
        const params = new URLSearchParams(window.location.search);
        params.set("createaccount", "true");
        router.replace(`/login?${params}`);
      }
      return;
    }

    if (userDoc.signup?.status === "pending") {
      if (pathname !== "/signup" && pathname !== "/signup/complete") {
        const params = new URLSearchParams(window.location.search);
        router.replace(pendingSignupUrl(params, userDoc.signup));
      } else {
        setIsNavigationComplete(true);
      }
      return;
    }
    if (!userDoc.onboarded) {
      if (pathname !== "/onboarding") router.replace("/onboarding");
      else setIsNavigationComplete(true);
      return;
    }
    if (
      pathname === "/login" ||
      pathname === "/onboarding" ||
      pathname === "/signup" ||
      pathname === "/signup/complete" ||
      pathname === "/reset"
    ) {
      const returnUrl = safeSignupReturnUrl(
        new URLSearchParams(window.location.search).get("returnUrl")
      );
      router.push(
        returnUrl && !returnUrl.startsWith("/signup") ? returnUrl : "/"
      );
      return;
    }
    setIsNavigationComplete(true);
  }, [authedUser, loading, isBookRoute, pathname, router, userDoc]);

  const isSignupRoute =
    pathname === "/signup" || pathname === "/signup/complete";
  const signupAllowsRoute =
    possibleUser?.signup?.status !== "pending" || isSignupRoute;
  const selectedPlanLoaded =
    possibleUser?.signup?.status !== "complete" || isSubscriptionReady;
  const shouldRenderMainContent =
    !loading && isNavigationComplete && signupAllowsRoute && selectedPlanLoaded;
  const isPublicRoute =
    pathname === "/login" ||
    pathname === "/reset" ||
    pathname === "/onboarding" ||
    isSignupRoute ||
    pathname === "/portal";

  return (
    <>
      <MarketingConversionTracker />
      {!shouldRenderMainContent ? (
        <AnimatedLoader bkgOpacity={1} />
      ) : isPublicRoute ? (
        children
      ) : (
        <DatesProvider
          settings={{ timezone: possibleUser?.settings?.timezone }}
        >
          {isPosTriggerEnabled(user) && <POSTrigger />}
          <TimeClockWidget />
          {/* Renders nothing unless the tenant's subscription is past due
              (users/{uid}/metadata/subscription.billing, kept by the billing
              webhook). Team members see it too: the API resolves them to the
              owner's subscription. */}
          <BillingOverdueModal />
          {/* Renders nothing unless the tenant still bills on the NZ Stripe
              account and needs to re-confirm their card on Atlas
              (subscription.nzMigration, see scripts/stripe-nz-to-atlas). */}
          <BillingMigrationModal />
          <AppShell>{children}</AppShell>
        </DatesProvider>
      )}
      <Suspense fallback={<AnimatedLoader bkgOpacity={1} />}>
        <NavigationEvents
          onNavigate={markNavigationComplete}
          user={possibleUser}
        />
      </Suspense>
      <Alert />
      {/* {isNavigationComplete && <ChristmasModal />} */}
    </>
  );
}
